<script setup lang="ts">
import { useAppStore } from '@/store/app/app'
import { SnackbarType } from '@/store/app/app.types'
import { useBasketStore } from '@/store/basket/basket'
import { useCityStore } from '@/store/city/city'

import { NewProductModification } from '@/types/api/api-v4/product'
import { EcommerceEventName } from '@/types/plugins/yandex-metrika'
import { SetProductIncludeType, SetProductType } from '@/types/store/category'

import ProductDetailButton from '@/components/Product/ProductDetail/ProductDetailButton.vue'

type Props = {
  currentModification: NewProductModification
}
const props = defineProps<Props>()

const { isMobile } = useResponsive()

const { showSnackbar } = useAppStore()
const basketStore = useBasketStore()
const { currencySymbol } = useCityStore()

const { pushMetrics } = useSendMetricsOfProduct(props.currentModification)

const priceValue = computed(() => {
  return `${props.currentModification.price} ${currencySymbol}`
})

const oldPriceValue = computed(() => {
  return `${props.currentModification.subprice} ${currencySymbol}`
})

const productId = computed(() => props.currentModification.id)

const {
  basketError,
  quantitySelectedProducts,
  setProductQuantity,
  decreaseQuantity,
  increaseQuantity,
} = useProductCounter(productId, props.currentModification)

const quantityInProductBasket = computed(() => {
  return (
    basketStore.isProductInBasket(props.currentModification.id)?.quantity || 0
  )
})

const onUpdateProductQuantity = () => {
  const quantity = quantitySelectedProducts.value

  setProductQuantity(quantity)
}

const addToCart = async (productId: number) => {
  setProductQuantity(quantitySelectedProducts.value + 1) // текущее + 1

  await basketStore.addProduct(productId)

  if (!basketError.value) {
    pushMetrics(EcommerceEventName.ADD, 1)

    return
  }

  setProductQuantity(quantityInProductBasket.value) // при ошибке возвращаем кол-во с сервера

  showSnackbar({
    message: 'Что-то пошло не так',
    type: SnackbarType.MESSAGE,
  })
}

const setProduct = (type: SetProductIncludeType) => {
  // Проверяем, какая кнопка counter-а была нажата и высчитываем измененнное кол- во продукта
  if (type === SetProductType.DECREASE) {
    decreaseQuantity()
    return
  }

  increaseQuantity()
}

const isVisibleBasketBtn = computed(() => {
  return !isMobile.value && quantitySelectedProducts.value
})

const showOldPrice = computed(() => {
  const { price, subprice } = props.currentModification

  return Number(subprice) > Number(price)
})
</script>

<template>
  <div class="price-panel" v-bind="$attrs">
    <div class="price-panel__prices">
      <div class="price-panel__price">{{ priceValue }}</div>

      <div v-if="showOldPrice" class="price-panel__old-price">
        {{ oldPriceValue }}
      </div>
    </div>

    <div class="price-panel__action-btn">
      <product-detail-button
        :product-quantity="quantitySelectedProducts"
        @add-to-cart="addToCart(currentModification.id)"
        @set-increase-product="setProduct(SetProductType.INCREASE)"
        @set-decrease-product="setProduct(SetProductType.DECREASE)"
        @update-product-quantity="onUpdateProductQuantity"
      />
    </div>
  </div>

  <the-button
    v-if="isVisibleBasketBtn"
    class="price-panel__basket-btn"
    variant="tertiary"
    to="/basket/"
  >
    Перейти в корзину
  </the-button>
</template>

<style scoped lang="scss">
.price-panel {
  position: sticky;
  bottom: 0;
  left: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  width: 100%;
  padding: 16px;
  background-color: var(--ui-white);
  border-radius: 20px 20px 0 0;
  box-shadow: 0px -6px 30px 0px #32344826;

  @include media('>=tablet') {
    position: relative;
    padding: 0;
    background-color: unset;
    border-radius: unset;
    box-shadow: none;
  }

  &__prices {
    width: max-content;
  }

  &__price {
    font-size: 20px;
    font-weight: 800;
    line-height: 26px;
    color: var(--ui-secondary);

    @include media('>=tablet') {
      font-size: 22px;
      line-height: 29px;
    }
  }

  &__old-price {
    font-size: 12px;
    font-weight: 600;
    line-height: 16px;
    color: var(--ui-dark-grey);
    text-decoration: line-through;

    @include media('>=tablet') {
      font-size: 14px;
      line-height: 18px;
    }
  }

  &__basket-btn {
    width: 100%;
    margin-top: 24px;
  }

  &__action-btn {
    margin-left: auto;
  }
}
</style>
